import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const SupplierLedger = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersWithPayable, setSuppliersWithPayable] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('payable'); // payable or all
  const [businessInfo, setBusinessInfo] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_mode: 'cash',
    reference: '',
    notes: ''
  });

  const [supplierForm, setSupplierForm] = useState({
    name: '',
    mobile: '',
    gst_number: '',
    address: '',
    city: ''
  });

  useEffect(() => {
    fetchSuppliers();
    fetchSuppliersWithPayable();
    fetchBusinessInfo();
  }, []);

  const fetchBusinessInfo = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/settings/business`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessInfo(response.data);
    } catch (err) {
      console.error('Error fetching business info:', err);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliersWithPayable = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/suppliers-with-payable`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliersWithPayable(response.data);
    } catch (err) {
      console.error('Error fetching payables:', err);
    }
  };

  const fetchSupplierLedger = async (supplierId) => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/supplier-ledger/${supplierId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLedger(response.data);
      setSelectedSupplier(response.data.supplier);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch ledger');
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    try {
      const token = localStorage.getItem('pos_token');
      await axios.post(`${API}/supplier-payment`, {
        supplier_id: selectedSupplier.id,
        amount: parseFloat(paymentForm.amount),
        payment_mode: paymentForm.payment_mode,
        reference: paymentForm.reference || null,
        notes: paymentForm.notes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Payment recorded successfully');
      setShowPaymentModal(false);
      setPaymentForm({ amount: '', payment_mode: 'cash', reference: '', notes: '' });
      fetchSupplierLedger(selectedSupplier.id);
      fetchSuppliersWithPayable();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleAddSupplier = async () => {
    if (!supplierForm.name || !supplierForm.mobile) {
      setError('Name and Mobile are required');
      return;
    }

    try {
      const token = localStorage.getItem('pos_token');
      await axios.post(`${API}/settings/suppliers`, supplierForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Supplier added successfully');
      setShowAddSupplierModal(false);
      setSupplierForm({ name: '', mobile: '', gst_number: '', address: '', city: '' });
      fetchSuppliers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add supplier');
    }
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toFixed(2)}`;

  // Filter transactions by date
  const getFilteredTransactions = () => {
    if (!ledger?.transactions) return [];
    
    return ledger.transactions.filter(txn => {
      const txnDate = new Date(txn.created_at).toISOString().split('T')[0];
      if (fromDate && txnDate < fromDate) return false;
      if (toDate && txnDate > toDate) return false;
      return true;
    });
  };

  const filteredTransactions = getFilteredTransactions();

  // Calculate running balance for filtered transactions
  const getTransactionsWithBalance = () => {
    let runningBalance = 0;
    return filteredTransactions.map(txn => {
      if (txn.balance_type === 'payable') {
        runningBalance += txn.amount;
      } else {
        runningBalance -= txn.amount;
      }
      return { ...txn, running_balance: runningBalance };
    });
  };

  // Export to PDF
  const handleExportPDF = async () => {
    if (!selectedSupplier || !ledger) return;
    
    setIsExporting(true);
    try {
      const transactions = getTransactionsWithBalance();
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      let y = 15;

      // Header - Business Info
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text(businessInfo?.business_name || 'Business Name', pageWidth / 2, y, { align: 'center' });
      y += 6;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      const address = [
        businessInfo?.address,
        businessInfo?.city,
        businessInfo?.district,
        businessInfo?.state,
        businessInfo?.postal_code
      ].filter(Boolean).join(', ');
      if (address) {
        pdf.text(address, pageWidth / 2, y, { align: 'center' });
        y += 4;
      }
      if (businessInfo?.mobile) {
        pdf.text(`Tel: ${businessInfo.mobile}`, pageWidth / 2, y, { align: 'center' });
        y += 4;
      }
      if (businessInfo?.gst_number) {
        pdf.text(`GSTIN: ${businessInfo.gst_number}`, pageWidth / 2, y, { align: 'center' });
        y += 4;
      }

      // Title
      y += 4;
      pdf.setDrawColor(100);
      pdf.line(15, y, pageWidth - 15, y);
      y += 6;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('SUPPLIER LEDGER STATEMENT', pageWidth / 2, y, { align: 'center' });
      y += 6;
      pdf.line(15, y, pageWidth - 15, y);
      y += 8;

      // Supplier Details
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Supplier Details:', 15, y);
      y += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Name: ${selectedSupplier.name}`, 15, y);
      pdf.text(`Mobile: ${selectedSupplier.mobile || 'N/A'}`, 110, y);
      y += 5;
      if (selectedSupplier.gst_number) {
        pdf.text(`GSTIN: ${selectedSupplier.gst_number}`, 15, y);
        y += 5;
      }
      if (selectedSupplier.address || selectedSupplier.city) {
        pdf.text(`Address: ${[selectedSupplier.address, selectedSupplier.city].filter(Boolean).join(', ')}`, 15, y);
        y += 5;
      }

      // Date Range & Export Time
      y += 3;
      const dateRange = fromDate || toDate 
        ? `Period: ${fromDate || 'Start'} to ${toDate || 'Present'}`
        : 'Period: All Transactions';
      pdf.text(dateRange, 15, y);
      pdf.text(`Exported: ${new Date().toLocaleString('en-IN')}`, pageWidth - 15, y, { align: 'right' });
      y += 8;

      // Summary Box
      pdf.setFillColor(245, 245, 245);
      pdf.rect(15, y, pageWidth - 30, 18, 'F');
      pdf.setDrawColor(200);
      pdf.rect(15, y, pageWidth - 30, 18, 'S');
      y += 6;
      pdf.setFontSize(9);
      const summaryY = y;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Total Purchases:', 20, summaryY);
      pdf.text('Total Payments:', 70, summaryY);
      pdf.text('Balance Payable:', 130, summaryY);
      y += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.text(formatCurrency(ledger.payable_total), 20, y);
      pdf.setTextColor(0, 128, 0);
      pdf.text(formatCurrency(ledger.payment_total), 70, y);
      pdf.setTextColor(50, 50, 50);
      pdf.setFont('helvetica', 'bold');
      pdf.text(formatCurrency(ledger.current_balance), 130, y);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      y += 12;

      // Table Header
      const colWidths = [25, 30, 35, 30, 30, 30];
      const startX = 15;
      pdf.setFillColor(50, 50, 50);
      pdf.setTextColor(255, 255, 255);
      pdf.rect(startX, y, pageWidth - 30, 8, 'F');
      y += 5;
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      let x = startX + 2;
      pdf.text('Date', x, y);
      x += colWidths[0];
      pdf.text('Type', x, y);
      x += colWidths[1];
      pdf.text('Reference', x, y);
      x += colWidths[2];
      pdf.text('Purchase', x + colWidths[3] - 5, y, { align: 'right' });
      x += colWidths[3];
      pdf.text('Payment', x + colWidths[4] - 5, y, { align: 'right' });
      x += colWidths[4];
      pdf.text('Balance', x + colWidths[5] - 5, y, { align: 'right' });
      y += 5;
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');

      // Table Rows
      transactions.forEach((txn, idx) => {
        if (y > 270) {
          pdf.addPage();
          y = 15;
        }

        // Alternate row colors
        if (idx % 2 === 0) {
          pdf.setFillColor(250, 250, 250);
          pdf.rect(startX, y - 3, pageWidth - 30, 7, 'F');
        }

        x = startX + 2;
        pdf.text(new Date(txn.created_at).toLocaleDateString('en-IN'), x, y);
        x += colWidths[0];
        pdf.text(txn.balance_type === 'payable' ? 'Purchase/GRN' : 'Payment', x, y);
        x += colWidths[1];
        pdf.text((txn.reference_number || txn.reference || txn.notes || '-').substring(0, 15), x, y);
        x += colWidths[2];
        
        if (txn.balance_type === 'payable') {
          pdf.text(formatCurrency(txn.amount), x + colWidths[3] - 5, y, { align: 'right' });
          x += colWidths[3];
          pdf.text('-', x + colWidths[4] - 5, y, { align: 'right' });
        } else {
          pdf.text('-', x + colWidths[3] - 5, y, { align: 'right' });
          x += colWidths[3];
          pdf.setTextColor(0, 128, 0);
          pdf.text(formatCurrency(txn.amount), x + colWidths[4] - 5, y, { align: 'right' });
          pdf.setTextColor(0, 0, 0);
        }
        x += colWidths[4];
        pdf.text(formatCurrency(txn.running_balance), x + colWidths[5] - 5, y, { align: 'right' });
        y += 7;
      });

      // Footer
      y += 10;
      if (y > 270) {
        pdf.addPage();
        y = 15;
      }
      pdf.setDrawColor(150);
      pdf.line(15, y, pageWidth - 15, y);
      y += 5;
      pdf.setFontSize(8);
      pdf.setTextColor(100);
      pdf.text('This is a computer generated statement.', pageWidth / 2, y, { align: 'center' });
      y += 4;
      pdf.text(`Generated by GPOS - ${new Date().toLocaleString('en-IN')}`, pageWidth / 2, y, { align: 'center' });

      // Save
      const fileName = `Supplier_Ledger_${selectedSupplier.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      setSuccess('PDF exported successfully!');
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError('Failed to export PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const displayList = activeTab === 'payable' 
    ? suppliersWithPayable.filter(s => s.supplier_name.toLowerCase().includes(search.toLowerCase()))
    : suppliers.filter(s => s.name?.toLowerCase().includes(search.toLowerCase()));

  const totalPayable = suppliersWithPayable.reduce((sum, s) => sum + s.balance, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="supplier-ledger-page" className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Supplier Ledger</h1>
            <p className="text-sm text-slate-500">Manage supplier payables and payments</p>
          </div>
          <button
            onClick={() => setShowAddSupplierModal(true)}
            className="px-3 py-2 text-sm border border-violet-200 text-violet-600 rounded-lg hover:bg-violet-50"
            data-testid="add-supplier-btn"
          >
            + Add
          </button>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <p className="text-sm text-slate-300">Total Payable</p>
          <p className="text-2xl font-bold text-white">{formatCurrency(totalPayable)}</p>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-500">×</button>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
          {success}
          <button onClick={() => setSuccess('')} className="ml-2 text-green-500">×</button>
        </div>
      )}

      <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Supplier List */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-3 sm:p-4 border-b border-slate-200 space-y-3">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('payable')}
                className={`flex-1 px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium ${
                  activeTab === 'payable' ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                With Payable
              </button>
              <button
                onClick={() => setActiveTab('all')}
                className={`flex-1 px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium ${
                  activeTab === 'all' ? 'bg-violet-100 text-violet-700' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                All
              </button>
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search supplier..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="search-supplier"
            />
          </div>
          <div className="max-h-[40vh] lg:max-h-[60vh] overflow-y-auto">
            {displayList.length === 0 ? (
              <div className="p-4 text-center text-slate-500 text-sm">
                {activeTab === 'payable' ? 'No suppliers with payable balance' : 'No suppliers found'}
              </div>
            ) : (
              displayList.map(supplier => (
                <button
                  key={supplier.supplier_id || supplier.id}
                  onClick={() => fetchSupplierLedger(supplier.supplier_id || supplier.id)}
                  className={`w-full text-left p-3 sm:p-4 border-b border-slate-100 hover:bg-violet-50 transition-colors ${
                    selectedSupplier?.id === (supplier.supplier_id || supplier.id) ? 'bg-violet-50' : ''
                  }`}
                  data-testid={`supplier-${supplier.supplier_id || supplier.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-800 text-sm truncate">{supplier.supplier_name || supplier.name}</p>
                      <p className="text-xs text-slate-500">{supplier.mobile}</p>
                    </div>
                    {supplier.balance !== undefined && (
                      <span className="text-slate-800 font-bold text-sm whitespace-nowrap">{formatCurrency(supplier.balance)}</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Ledger Details */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
          {!selectedSupplier ? (
            <div className="p-6 sm:p-8 text-center text-slate-500">
              <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p className="text-sm">Select a supplier to view ledger</p>
            </div>
          ) : (
            <>
              <div className="p-3 sm:p-4 border-b border-slate-200 bg-slate-50">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-base sm:text-lg">{selectedSupplier.name}</h3>
                    <p className="text-xs sm:text-sm text-slate-500">{selectedSupplier.mobile} {selectedSupplier.city && `• ${selectedSupplier.city}`}</p>
                    {selectedSupplier.gst_number && (
                      <p className="text-xs text-slate-400">GST: {selectedSupplier.gst_number}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleExportPDF}
                      disabled={isExporting}
                      className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm flex items-center gap-1 disabled:opacity-50"
                      data-testid="export-supplier-pdf-btn"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      {isExporting ? '...' : 'PDF'}
                    </button>
                    <button
                      onClick={() => setShowPaymentModal(true)}
                      className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-900 text-sm"
                      data-testid="record-supplier-payment-btn"
                    >
                      Make Payment
                    </button>
                  </div>
                </div>
              </div>

              {/* Date Filters */}
              <div className="p-3 border-b border-slate-200 bg-slate-50/50 flex flex-wrap items-center gap-2 text-sm">
                <span className="text-slate-600 font-medium">Filter:</span>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="supplier-from-date"
                />
                <span className="text-slate-400">to</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="supplier-to-date"
                />
                {(fromDate || toDate) && (
                  <button
                    onClick={() => { setFromDate(''); setToDate(''); }}
                    className="text-xs text-violet-600"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Balance Summary - Mobile optimized */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 p-3 sm:p-4 border-b border-slate-200">
                <div className="text-center p-2 sm:p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Purchases</p>
                  <p className="text-sm sm:text-xl font-bold text-slate-800">{formatCurrency(ledger?.payable_total)}</p>
                </div>
                <div className="text-center p-2 sm:p-3 bg-emerald-50 rounded-lg">
                  <p className="text-xs text-emerald-600">Paid</p>
                  <p className="text-sm sm:text-xl font-bold text-emerald-700">{formatCurrency(ledger?.payment_total)}</p>
                </div>
                <div className="text-center p-2 sm:p-3 bg-slate-100 rounded-lg">
                  <p className="text-xs text-slate-600">Payable</p>
                  <p className="text-sm sm:text-xl font-bold text-slate-800">{formatCurrency(ledger?.current_balance)}</p>
                </div>
              </div>

              {/* Transactions - Mobile card view on small screens */}
              <div className="max-h-[35vh] sm:max-h-[40vh] overflow-y-auto">
                {/* Desktop Table */}
                <table className="w-full hidden sm:table">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Reference</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {ledger?.transactions?.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                          No transactions yet
                        </td>
                      </tr>
                    ) : (
                      ledger?.transactions?.map((txn, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm text-slate-600">
                            <div>{new Date(txn.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                            <div className="text-xs text-slate-400">{new Date(txn.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              txn.balance_type === 'payable' ? 'bg-slate-200 text-slate-800' : 'bg-emerald-100 text-emerald-700'
                            }`}>
                              {txn.balance_type === 'payable' ? 'Purchase/GRN' : 'Payment'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {txn.reference_number || txn.reference || txn.notes || '-'}
                          </td>
                          <td className={`px-4 py-3 text-right font-medium ${
                            txn.balance_type === 'payable' ? 'text-slate-800' : 'text-emerald-600'
                          }`}>
                            {txn.balance_type === 'payable' ? '+' : '-'}{formatCurrency(txn.amount)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {/* Mobile Card View */}
                <div className="sm:hidden p-3 space-y-2">
                  {ledger?.transactions?.length === 0 ? (
                    <div className="text-center text-slate-500 py-6 text-sm">
                      No transactions yet
                    </div>
                  ) : (
                    ledger?.transactions?.map((txn, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            txn.balance_type === 'payable' ? 'bg-slate-200 text-slate-800' : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {txn.balance_type === 'payable' ? 'Purchase' : 'Payment'}
                          </span>
                          <span className={`font-bold text-sm ${
                            txn.balance_type === 'payable' ? 'text-slate-800' : 'text-emerald-600'
                          }`}>
                            {txn.balance_type === 'payable' ? '+' : '-'}{formatCurrency(txn.amount)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>
                            {new Date(txn.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} {new Date(txn.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <span className="truncate max-w-[120px]">{txn.reference_number || txn.reference || '-'}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold">Make Payment</h3>
              <p className="text-sm text-slate-500">to {selectedSupplier?.name}</p>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <p className="text-sm text-slate-600 mb-2">Balance Payable: 
                  <span className="font-bold text-slate-800 ml-2">{formatCurrency(ledger?.current_balance)}</span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="supplier-payment-amount"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Payment Mode</label>
                <select
                  value={paymentForm.payment_mode}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_mode: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="supplier-payment-mode"
                >
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reference (Optional)</label>
                <input
                  type="text"
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                  placeholder="Cheque No, UTR, etc."
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="supplier-payment-reference"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecordPayment}
                  className="flex-1 bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-900"
                  data-testid="confirm-supplier-payment-btn"
                >
                  Make Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Supplier Modal */}
      {showAddSupplierModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold">Add New Supplier</h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Supplier Name *</label>
                <input
                  type="text"
                  value={supplierForm.name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="supplier-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mobile *</label>
                <input
                  type="text"
                  value={supplierForm.mobile}
                  onChange={(e) => setSupplierForm({ ...supplierForm, mobile: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="supplier-mobile-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">GST Number (Optional)</label>
                <input
                  type="text"
                  value={supplierForm.gst_number}
                  onChange={(e) => setSupplierForm({ ...supplierForm, gst_number: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="supplier-gst-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">City (Optional)</label>
                <input
                  type="text"
                  value={supplierForm.city}
                  onChange={(e) => setSupplierForm({ ...supplierForm, city: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="supplier-city-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Address (Optional)</label>
                <textarea
                  value={supplierForm.address}
                  onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })}
                  rows="2"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="supplier-address-input"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowAddSupplierModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSupplier}
                  className="flex-1 bg-violet-600 text-white px-4 py-2 rounded-lg hover:bg-violet-700"
                  data-testid="save-supplier-btn"
                >
                  Add Supplier
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierLedger;
